<template>
  <!--
    GINGER-20260629: Kazang V2 merchant-session banner (v1.2.0).

    Mount this on the approval page (Dashboard.vue / DeptManager.vue). On mount
    it probes the session status; if no active session, it shows the "Start
    session" SweetAlert. While active, it polls /countdown every second and
    renders a small banner with the remaining time.

    Renders nothing visible if no wallet is bound yet. Reuses DepositOtpPrompt
    for the OTP entry modal — same UX as per-deposit OTP, different trigger.

    See docs/kazang_v2_session_otp_spec_20260629.md for the protocol.
  -->
  <div v-if="effectiveWalletId && isSessionActive" class="session-banner session-active" role="status">
    <span class="dot"></span>
    <span class="label">Session active</span>
    <span class="remaining">{{ remainingLabel }}</span>
    <button class="restart-btn" @click="restartSession" title="Force restart session">↻</button>
  </div>
  <div v-else-if="effectiveWalletId && countdownTimer" class="session-banner session-pending" role="status">
    <span class="dot pulse"></span>
    <span class="label">Session starting…</span>
    <span class="remaining">{{ remainingLabel }}</span>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import DepositOtpPrompt from '@/components/DepositOtpPrompt.vue';

export default {
  name: 'SessionManager',
  components: { DepositOtpPrompt },
  props: {
    walletId: { type: Number, default: null },
  },
  computed: {
    // GINGER-20260629: pull walletId from Vuex selectedWallet if not passed as prop.
    // This lets the parent views just drop <SessionManager /> without plumbing walletId.
    effectiveWalletId() {
      if (this.walletId) return this.walletId;
      return this.$store.getters.getSelectedWallet;
    },
    isSessionActive() {
      return !!(this.status && this.status.active);
    },
    remainingLabel() {
      return (this.status && this.status.remainingLabel) || '0s';
    },
    // GINGER-20260630 (Jabulani): dismissal lives in Vuex so it survives component
    // remounts when the merchant navigates Dashboard ↔ DeptManager.
    dismissed() {
      return this.$store.getters.wasPromptDismissed;
    },
  },
  data() {
    return {
      // Local mirror of the Vuex status so we can render without re-dispatching.
      status: null,
      countdownTimer: null,
    };
  },
  async mounted() {
    if (!this.effectiveWalletId) return;
    await this.checkStatus();
    if (!this.isSessionActive && !this.dismissed) {
      // Defer the prompt so it doesn't block the page render.
      setTimeout(() => this.maybePromptStart(), 800);
    }
  },
  beforeDestroy() {
    this.stopCountdown();
  },
  watch: {
    isSessionActive(active) {
      if (active) this.startCountdown();
      else this.stopCountdown();
    },
    effectiveWalletId() {
      // Re-probe if the wallet changes (e.g. user switched active wallet).
      this.checkStatus();
    },
  },
  methods: {
    async checkStatus() {
      try {
        const status = await this.$store.dispatch('getMerchantSessionStatus', {
          walletId: this.effectiveWalletId,
          remote: true,  // first probe of the session = hit Kazang for truth
        });
        this.status = status;
        if (this.isSessionActive) this.startCountdown();
      } catch (e) {
        // Silent — if Kazang is down, the approval path itself will fail loudly.
        this.status = { phase: 'none', active: false, remainingSeconds: 0, remainingLabel: '0s' };
      }
    },
    async maybePromptStart() {
      if (this.isSessionActive) return;
      const result = await Swal.fire({
        title: 'Start a Kazang session?',
        html: `
          <p class="mb-2">To approve deposits for the next <strong>2 hours</strong> without
          typing an OTP every time, start a session now.</p>
          <p class="text-muted small mb-0">Kazang will send a 6-digit code to the merchant's
          WhatsApp. One code covers all approvals in the 2-hour window.</p>
        `,
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: 'Start session',
        cancelButtonText: 'Not now',
        allowOutsideClick: false,
        allowEscapeKey: false,
      });
      if (!result.isConfirmed) {
        // GINGER-20260630: commit dismissal to Vuex so navigation to other
        // approval pages won't re-prompt. Cleared automatically when a new
        // session is confirmed or when the 2-hour window expires.
        this.$store.commit('setDismissedSessionPrompt', true);
        return;
      }
      await this.startSessionFlow();
    },
    async startSessionFlow() {
      // Show the SweetAlert as a loading backdrop so the user can't double-click.
      Swal.fire({
        title: 'Starting session…',
        html: 'Contacting Kazang',
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading(),
      });
      try {
        // Step 1: start the session (Kazang sends OTP to merchant WhatsApp).
        const start = await this.$store.dispatch('startMerchantSession', this.effectiveWalletId);
        Swal.close();
        if (!start || !start.sessionRequestId) {
          throw new Error('startMerchantSession did not return sessionRequestId');
        }
        // Step 2: prompt merchant for OTP (reuses the existing modal).
        const otpPrompt = this.$options.components?.DepositOtpPrompt || DepositOtpPrompt;
        let otp;
        try {
          ({ otp } = await otpPrompt.show({
            phoneHint: start.phoneHint,
            expiresAt: start.expiresAt,
            userRequestId: null,  // session OTP, not user_request
          }));
        } catch (e) {
          if (e && e.message === 'cancelled') {
            // User cancelled — leave them in the "no session" state, banner will re-prompt.
            return;
          }
          throw e;
        }
        // Step 3: confirm OTP — backend caches sessionId for 2hr.
        await this.$store.dispatch('confirmMerchantSessionOtp', {
          walletId: this.effectiveWalletId,
          sessionRequestId: start.sessionRequestId,
          otp,
        });
        await this.checkStatus();
        // Toast confirmation
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: `Session active for 2 hours`,
          showConfirmButton: false,
          timer: 3500,
        });
      } catch (e) {
        Swal.fire({
          icon: 'error',
          title: 'Could not start session',
          text: (e && e.message) || 'Try again, or check your connection.',
        });
      }
    },
    async restartSession() {
      // User-initiated restart from the banner. Useful if they think the
      // session got into a bad state (rare — backend auto-invalidates on 401).
      this.status = null;
      await this.startSessionFlow();
    },
    startCountdown() {
      this.stopCountdown();
      // Per Grace's spec: "Poll once per second while the user is in either
      // the OTP step (phase=otp) or the active session (phase=session). Stop
      // when phase flips to none or expired (or when active=false AND the
      // user dismisses the modal)."
      this.countdownTimer = setInterval(async () => {
        if (!this.isSessionActive) {
          this.stopCountdown();
          return;
        }
        try {
          const status = await this.$store.dispatch('getMerchantSessionCountdown', this.effectiveWalletId);
          this.status = status;
          if (!status || !status.active) this.stopCountdown();
        } catch (e) {
          // Don't crash the UI on a transient network blip — the approval path
          // itself will surface real failures.
        }
      }, 1000);
    },
    stopCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    },
  },
};
</script>

<style scoped>
.session-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 14px;
  margin: 8px 0 12px;
  user-select: none;
}
.session-active {
  background: #e6f4ea;
  border: 1px solid #34a853;
  color: #1e4620;
}
.session-pending {
  background: #fef7e0;
  border: 1px solid #fbbc04;
  color: #5f4b00;
}
.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #34a853;
  display: inline-block;
}
.dot.pulse {
  background: #fbbc04;
  animation: pulse 1.4s infinite;
}
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
}
.label {
  font-weight: 600;
}
.remaining {
  margin-left: auto;
  font-variant-numeric: tabular-nums;
  font-weight: 500;
}
.restart-btn {
  border: 1px solid #1e4620;
  background: transparent;
  color: #1e4620;
  border-radius: 4px;
  padding: 2px 8px;
  cursor: pointer;
  font-size: 13px;
  line-height: 1;
}
.restart-btn:hover { background: #1e4620; color: #fff; }
</style>
